import { useMeters } from "@features/mx/routeManagement/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { TableColumn } from "@shared/types/table";
import { Card } from "@shared/ui/card";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { BarChart3, Wrench } from "lucide-react";
import { useState } from "react";


interface ReadingRecord {
  id: number;
  date: string;
  month: string;
  readType: string;
  reading: number;
  status: 'valid' | 'invalid';
  reader: string;
  validator: string;
}

interface ResourcesTableProps {
  selectedPremises?: string[];
  resourceTypeCode?: any;
  resourceSubTypeCode?: any;
  category?: any;
  subCategory?: any;
}

export const ResourcesTable = ({
  selectedPremises,
  resourceTypeCode,
  resourceSubTypeCode,
  category,
  subCategory,
}: ResourcesTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data: metersList, isLoading } = useMeters(
    selectedPremises,
    currentPage,
    MODAL_PAGE_SIZE,
    resourceTypeCode,
    resourceSubTypeCode,
    category,
    subCategory
  );
  const metersData = metersList?.results || [];
  const totalCount = metersList?.count || 0;


  const columns: TableColumn<ReadingRecord>[] = [
    {
      key: "meterNumber",
      header: "Resource ID",
      headerTooltip: "Unique identifier for the resources. Used for tracking readings and maintenance history."
    },
    {
      key: "deviceNo",
      header: "Serial No",
      headerTooltip: "Hardware serial number. Used for device-specific troubleshooting and warranty tracking."
    },
    {
      key: "productCode",
      header: "Utility Service",
      headerTooltip: "The utility service associated with this resource.",
      type: 'badge'
    },
    {
      key: "resourceTypeDisplay",
      header: "Type",
      headerTooltip: "Category or primary classification of the resource."
    },
    {
      key: "resourceSubtypeDisplay",
      header: "Sub Type",
      headerTooltip: "Specific sub-category or detailed classification of the resource."
    },
    {
      key: "premiseName",
      header: "Premise",
      headerTooltip: "Physical location where this resource is installed."
    },
    {
      key: "unit",
      header: "Unit",
      sortable: true,
      headerTooltip: "Measurement or structural unit associated with this resource."
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Card className="shadow-sm">
      <div className="space-y-6">
        <GenericTable
          data={metersData}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            currentPage,
            totalPages: Math.ceil(totalCount / MODAL_PAGE_SIZE),
            totalItems: totalCount,
            itemsPerPage: MODAL_PAGE_SIZE,
            startItem: (currentPage - 1) * MODAL_PAGE_SIZE + 1,
            endItem: Math.min(currentPage * MODAL_PAGE_SIZE, totalCount)
          }}
          onPageChange={handlePageChange}
          searchPlaceholder="Search meters..."
          emptyMessage="No meters found"
        />
      </div>
    </Card>);
};